import React, { useState } from 'react';
import { Dialog, DialogActions, DialogContent, FormControl, FormHelperText, Grid, LinearProgress, MenuItem, Select, Box, Chip, DialogTitle, SelectChangeEvent, Typography, Paper, IconButton, Button } from '@mui/material';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CancelIcon from "@mui/icons-material/Cancel";
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { Formik, Form } from 'formik';
import { InputLabel } from '@mui/material';
import { PartnerType } from '../../models/PartnersModel';
import { StyledDropzone } from '../Elements/Dropzone';
import * as yup from 'yup';
import { apiClient } from '../../helper/api';
import useAuth from '../../context/AuthProvider';
import Swal from 'sweetalert2';
import { Months } from '../../helper/utils';
import { GreenBtnColor, OrangeBtnColor, PrimaryBlueColor } from '../../helper/theme';


export interface DocumentTypes {
    documentTypeId: number;
    documentName: string;
};
export interface States {
    stateId: number;
    stateName: string;
};

interface Iprops{
    Open: boolean,
    handleClose: (month?: number | null, year?: number | null) => void,
    currentPartner: PartnerType | null
};

const AddNewDiligentSearchFolderValidationSchema = yup.object({
    // partnersId: yup.number().required('Customer/Partner is required').min(1, 'Please select a Customer/Partner'),
    year: yup.string().required('Year is required'),
    month: yup.string().required('Month is required'),
    // insuredName: yup.string().required('Insured Name is required'),
    // policyNumber: yup.string().required('Policy Number is required'),
    // stateId: yup.mixed().required('State is required').test('is-selected', 'State is required', (value) => {
    //     return value !== 0 && value !== null && value !== undefined;
    // }),
    // documentType: yup.mixed(), // Optional - not required
    files: yup.array().of(yup.mixed<File>()), // Optional - no validation required
});

const AddNewDiligentSearchFolder: React.FC<Iprops> = ({ Open, handleClose, currentPartner }) => {
    const { user } = useAuth();
    const token = user?.token;
    const [loading, setLoading] = useState(false);
    // const [documentTypes, setDocumentTypes] = useState<DocumentTypes[]>([]);
    // const [states, setStates] = useState<States[]>([]);
    
    const initialValues = {
        partnersId: currentPartner?.partnersId || null,
        year: new Date().getFullYear().toString(),
        month: (new Date().getMonth() + 1).toString(),

        // insuredName: '',
        // policyNumber: '',
        // stateId: 0,
        // documentTypeId: 0,

        files: [] as File[],
    };
    
    // useEffect(() => {
    //     getAllDocumentTypes();
    //     getAllStates();
    // // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, []);
    
    const getAllDocumentTypes = () => {
        setLoading(true);
        apiClient('/DocumentType/getAllDocumentType', {
            method: 'GET',
            headers: new Headers({
                'Authorization': `Bearer ${token}`,
            }),
        }).then((response) => {
            if (!response) return;
            // setDocumentTypes(response.data);
        }).catch((error) => {
            console.error('Error getting document types:', error);
            Swal.fire({
                title: 'Error',
                text: error.message || 'Failed to get document types.',
                icon: 'error',
            });
        }).finally(() => {
            setLoading(false);
        });
    };
    const getAllStates = () => {
        setLoading(true);
        apiClient('/States/getStates', {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            // setStates(responseJson.data);
        }).catch(error => {
            console.error("Error fetching states:", error)
        }).finally(() => {
            setLoading(false);
        });
    };
    
    const onCloseDialog = () => handleClose();

    return (
        <Dialog
            open={Open}
            onClose={onCloseDialog}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    width: '90%',
                    maxWidth: 900,
                    minHeight: '65vh',
                    maxHeight: '90vh',
                    borderRadius: 2,
                    boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
                },
            }}
            sx={{ '& .MuiDialog-paper': { overflow: 'hidden' } }}
        >
            {loading && <LinearProgress sx={{ flexShrink: 0 }} />}
            <DialogTitle
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 2,
                    px: 3,
                    background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <FolderOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
                    <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                        Add New Diligent Search Folder for <strong>{currentPartner?.customerName}</strong>
                    </Typography>
                </Box>
                <IconButton
                    onClick={onCloseDialog}
                    sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' } }}
                    aria-label="Close dialog"
                >
                    <CloseOutlinedIcon />
                </IconButton>
            </DialogTitle>
            
            <Formik initialValues={initialValues}
                enableReinitialize={true}
                validateOnChange={true}
                validationSchema={AddNewDiligentSearchFolderValidationSchema}
                onSubmit={(values, { setSubmitting, setErrors }) => {
                    setSubmitting(true);
                    setErrors({});
                    setLoading(true);
                    
                    // Prepare FormData for API call with files and form data
                    const formData = new FormData();
                    
                    // Append JSON data as a string field (will be in request.body)
                    formData.append("data", JSON.stringify({  // everything except files
                        partnersId: values.partnersId,
                        year: parseInt(values.year),
                        month: parseInt(values.month),
                        // insuredName: values.insuredName,
                        // policyNumber: values.policyNumber,
                        // stateId: values.stateId,
                        // documentTypeId: values.documentTypeId,
                    }));
                    
                    // Append files - all files with the same field name for multer
                    // Multer will put them in request.files as an array
                    if (values.files && values.files.length > 0) {
                        values.files.forEach((file) => {
                            formData.append("files", file);
                        });
                    }
                    
                    apiClient('/DiligentSearch/uploadDiligentSearchLibraryDocuments', {
                        method: 'POST',
                        headers: new Headers({
                            // Don't set Content-Type header - browser will set it with boundary for FormData
                            'Authorization': `Bearer ${token}`,
                        }),
                        body: formData,
                    }).then((response) => {
                        if (!response) return;
                        if (response.status) {
                            Swal.fire({
                                title: 'Folder uploaded successfully',
                                text: 'The folder has been uploaded successfully.',
                                icon: 'success',
                            });
                            handleClose(parseInt(values.month), parseInt(values.year));
                        } else {
                            throw new Error(response.message || 'Failed to upload folder');
                        }
                    }).catch((error) => {
                        console.error('Error uploading folder:', error);
                        Swal.fire({
                            title: 'Error',
                            text: error.message || 'Failed to upload folder.',
                            icon: 'error',
                        });
                    }).finally(() => {
                        setSubmitting(false);
                        setLoading(false);
                    });
                }}
            >
                {({ values, isSubmitting, setFieldValue, setFieldTouched, errors, touched, isValid }) => {
                    const handleChange = (e: SelectChangeEvent<string | number> | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                        const { name, value } = e.target;
                        setFieldValue(name, value);
                        setFieldTouched(name, true);
                    };
                    return (
                        <Form className='main-form' noValidate>
                            <DialogContent sx={{ width: '100%', minHeight: '50vh', overflow: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                                <Grid container spacing={3}>
                                    {/* Section: Folder period (Month & Year) */}
                                    <Grid item xs={12}>
                                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                                            Folder period
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: 'divider', backgroundColor: 'grey.50' }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={6}>
                                                    <FormControl fullWidth size="small" error={touched.month && !!errors.month}>
                                                        <InputLabel>Month</InputLabel>
                                                        <Select value={values.month} name="month"
                                                            onChange={handleChange}
                                                            onBlur={() => setFieldTouched('month', true)}
                                                            label="Month"
                                                            IconComponent={ExpandMoreIcon}
                                                            // sx={{ height: '2.8rem' }}
                                                            sx={{
                                                                '& .MuiSelect-select': { padding: '16px' },
                                                                '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px', paddingTop: '18px', paddingBottom: '18px', top: '-7px' },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' }
                                                            }}
                                                        >
                                                            {Object.keys(Months).map((month) => (
                                                                <MenuItem key={month} value={parseInt(month)}>
                                                                    {Months[parseInt(month) as keyof typeof Months]}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                        {touched.month && errors.month && (<FormHelperText>{errors.month}</FormHelperText>)}
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <FormControl fullWidth size="small" error={touched.year && !!errors.year}>
                                                        <InputLabel>Year</InputLabel>
                                                        <Select value={values.year} name="year"
                                                            onChange={handleChange}
                                                            onBlur={() => setFieldTouched('year', true)}
                                                            label="Year"
                                                            IconComponent={ExpandMoreIcon}
                                                            // sx={{ height: '2.8rem' }}
                                                            sx={{
                                                                '& .MuiSelect-select': { padding: '16px' },
                                                                '& .MuiOutlinedInput-notchedOutline': { border: '2px solid #e2e8f0 !important', borderRadius: '8px', paddingTop: '18px', paddingBottom: '18px', top: '-7px' },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0, 0, 0, 0.87) !important' },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2 !important' }
                                                            }}
                                                        >
                                                            {Array.from({ length: 10 }, (_, i) => {
                                                                const year = new Date().getFullYear() - i;
                                                                return (
                                                                    <MenuItem key={year} value={year.toString()}>{year}</MenuItem>
                                                                );
                                                            })}
                                                        </Select>
                                                        {touched.year && errors.year && (<FormHelperText>{errors.year}</FormHelperText>)}
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Grid>
                                    {/* Select Insured Name */}
                                    {/* <Grid item xs={6}>
                                        <FormControl fullWidth size="small">
                                            <TextField value={values.insuredName} name="insuredName"
                                                onChange={handleChange}
                                                onBlur={() => setFieldTouched('insuredName', true)}
                                                label="Insured Name"
                                                fullWidth
                                                error={touched.insuredName && !!errors.insuredName}
                                                helperText={touched.insuredName && errors.insuredName}
                                            />
                                        </FormControl>
                                    </Grid> */}
                                    {/* Select Policy Number */}
                                    {/* <Grid item xs={6}>
                                        <FormControl fullWidth size="small">
                                            <TextField value={values.policyNumber} name="policyNumber"
                                                onChange={handleChange}
                                                onBlur={() => setFieldTouched('policyNumber', true)}
                                                label="Policy Number"
                                                fullWidth
                                                error={touched.policyNumber && !!errors.policyNumber}
                                                helperText={touched.policyNumber && errors.policyNumber}
                                            />
                                        </FormControl>
                                    </Grid> */}
                                    {/* Select State */}
                                    {/* NOTE: CHANGE TO STATE_ID */}
                                    {/* <Grid item xs={6}>
                                        <FormControl fullWidth size="small" error={touched.stateId && !!errors.stateId}>
                                            <InputLabel sx={{ 
                                                transform: values.stateId ? 'translate(14px, -9px) scale(0.75)' : 'translate(14px, calc(50% + 1px)) scale(1)',
                                                transformOrigin: 'top left',
                                                '&.Mui-focused': {
                                                    transform: values.stateId ? 'translate(14px, -9px) scale(0.75)' : 'translate(14px, -9px) scale(0.75)',
                                                },
                                            }}>State</InputLabel>
                                            <Select value={values.stateId} name="stateId"
                                                onChange={handleChange}
                                                onBlur={() => setFieldTouched('stateId', true)}
                                                label="State"
                                                IconComponent={ExpandMoreIcon}
                                                sx={{ height: '2.8rem' }}
                                            >
                                                {states.map((state) => (
                                                    <MenuItem key={state.stateId} value={state.stateId}>
                                                        {state.stateName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            {touched.stateId && errors.stateId && (<FormHelperText>{errors.stateId}</FormHelperText>)}
                                        </FormControl>
                                    </Grid> */}
                                    {/* Select Document Type - not required */}
                                    {/* <Grid item xs={6}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel sx={{ 
                                                transform: values.documentTypeId ? 'translate(14px, -9px) scale(0.75)' : 'translate(14px, calc(50% + 1px)) scale(1)',
                                                transformOrigin: 'top left',
                                                '&.Mui-focused': {
                                                    transform: values.documentTypeId ? 'translate(14px, -9px) scale(0.75)' : 'translate(14px, -9px) scale(0.75)',
                                                }
                                            }}>Document Type</InputLabel>
                                            <Select value={values.documentTypeId}
                                                onChange={(e) => setFieldValue('documentTypeId', e.target.value)}
                                                label="Document Type"
                                                IconComponent={ExpandMoreIcon}
                                                sx={{ height: '2.8rem' }}
                                            >
                                                {documentTypes.map((documentType) => (
                                                    <MenuItem key={documentType.documentTypeId} value={documentType.documentTypeId}>
                                                        {documentType.documentName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid> */}
                                    
                                    {/* Section: Attachments */}
                                    <Grid item xs={12}>
                                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', display: 'block', mb: 1 }}>
                                            Attachments
                                        </Typography>
                                        <FormControl sx={{ width: '100%' }}
                                            error={touched.files && !!errors.files}
                                        >
                                            <FormHelperText className='formtexthelper-heading' sx={{ mb: 0.5 }}>
                                                Select file(s) or drag and drop
                                            </FormHelperText>
                                            <StyledDropzone label="Select File(s) or Drag and Drop" 
                                                onFileDrop={(newFiles: File[]) => {
                                                    const existingFiles = values.files || [];
                                                    const updatedFiles = [...existingFiles, ...newFiles];
                                                    setFieldValue("files", updatedFiles);
                                                    setFieldTouched("files", true);
                                                }} 
                                            />
                                            {/* Display selected files */}
                                            {values.files && values.files.length > 0 && (
                                                <Box sx={{ mt: 2 }}>
                                                    <FormHelperText sx={{ mb: 1 }}>
                                                        Selected files ({values.files.length})
                                                    </FormHelperText>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                        {values.files.map((file, index) => (
                                                            <Chip key={index} label={`${file.name}`}
                                                                onDelete={() => {
                                                                    const updatedFiles = values.files.filter((_, i) => i !== index);
                                                                    setFieldValue("files", updatedFiles);
                                                                    setFieldTouched("files", true);
                                                                }}
                                                                deleteIcon={<CancelIcon />}
                                                                color="primary"
                                                                variant="outlined"
                                                                sx={{ 
                                                                    maxWidth: '100%',
                                                                    '& .MuiChip-label': {
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap',
                                                                    }
                                                                }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                            {touched.files && errors.files && (
                                                <FormHelperText error>
                                                    {typeof errors.files === 'string' ? errors.files : Array.isArray(errors.files) ? errors.files.join(', ') : 'Files are required'}
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions
                                sx={{
                                    px: 3,
                                    py: 2,
                                    gap: 1.5,
                                    borderTop: 1,
                                    borderColor: 'divider',
                                    backgroundColor: 'grey.50',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    onClick={onCloseDialog}
                                    startIcon={<CloseOutlinedIcon />}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        borderColor: '#e2e8f0',
                                        color: '#475569',
                                        '&:hover': {
                                            borderColor: '#2099d2',
                                            backgroundColor: '#2099d214',
                                            color: '#2099d2',
                                        },
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    type="submit"
                                    disabled={isSubmitting}
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        backgroundColor: '#2099d2',
                                        '&:hover': { backgroundColor: '#176990' },
                                        px: 2.5,
                                    }}
                                >
                                    Upload Folder
                                </Button>
                            </DialogActions>
                        </Form>
                    );
                }}
            </Formik>
        </Dialog>
    );
};
export default AddNewDiligentSearchFolder;